/**
 * Owns standalone asset filtering, selection, approval, and pagination while delegating
 * the shared table-toolbar presentation to the assets action bar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'src/hooks/useDebounce';
import { chunk } from 'lodash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import chevronLeftSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-left-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { PageLoader } from 'src/components/common/loaders/page-loader';
import Loader from '@amzn/meridian/loader';
import { AssetApprovalModal } from 'src/components/assets/modals/AssetApprovalModal';
import { BulkScheduleOverrideModal } from 'src/components/assets/modals/BulkScheduleOverrideModal';
import {
  type AssetStatus,
  ASSET_STATUSES,
} from 'src/components/assets/asset-status';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { ASSET_UPDATE_BATCH_SIZE, ConfigurationType } from 'src/constants';
import { Logger } from 'src/utils/logger';
import {
  type AssetDto,
  type AssetsControllerUpdateApiResponse,
  type BusinessLine,
  type CampaignConfigurationControllerFindManyApiArg,
  type CampaignDesiredStateDto,
  type CampaignTitlesControllerGetCampaignTitlesApiArg,
  type TemplateScheduleWindow,
} from 'src/store/generated/leela-api-generated';
import {
  useAssetsControllerFindManyQuery,
  useAssetsControllerUpdateMutation,
  useCampaignTitlesControllerGetCampaignTitlesBatchMutation,
  useDistinctValuesControllerGetDistinctValuesQuery,
  useAssetsControllerGetDistinctCampaignOptionsQuery,
  useCampaignConfigurationControllerFindManyQuery,
} from 'src/store/leela-api';
import { AssetsActionBar } from './AssetsActionBar';
import type { ContentRuleConfigValue, TypedCampaignConfigurationDto } from 'src/store/leela-api';
import type { RuleMap } from 'src/types/assetEval';
import { isAssetFlagged } from 'src/utils/assetEvalBadge';
import { ShowingCount } from 'src/components/common/data-table-action-bar/ShowingCount';
import { AssetTable } from './AssetTable';
import { AssetDetailView } from './AssetDetailView';
import { AssetPendingView } from './AssetPendingView';
import { useAvailablePublishers } from 'src/hooks/use-available-publishers';
import { AssetDetailApprovalModal } from './modals/AssetDetailApprovalModal';
import { type SortDirection } from '@amzn/meridian/data-table/types/column-sort';
import { normalizeAssetsCampaignIds } from 'src/utils/assetDeduplication';
import { assetStrategyKey, isSameAssetStrategy } from 'src/utils/assetKeys';
import { buildStrategyLabelMap, buildStrategyFilterOptions } from 'src/utils/strategyLabels';
import { buildLocaleFilter, buildAssetTypeFilter, PUBLISHER_ALL } from 'src/utils/assetFilterUtils';
import { computeAssetSchedule } from 'src/utils/computeAssetSchedule';
import { computeScheduleWarnings } from 'src/utils/computeScheduleWarnings';
import {
  groupAssetsForBulkSchedule,
  isBulkScheduleEligibleAsset,
  type BulkScheduleEventInfo,
  type BulkScheduleGroup,
  type Schedule,
} from 'src/utils/groupAssetsForBulkSchedule';
import { isSportsBusinessLine } from 'src/store/constants';
import { useCampaignTitleReleaseDates } from 'src/hooks/useCampaignTitleReleaseDates';

export interface StandaloneAssetsContainerProps {
  approvalStatus: AssetStatus;
  onTabChange: (tab: AssetStatus) => void;
}

const itemsPerPageOptions = [50, 100, 200, 500, 1000];

/** Upper bound for schedule-warning and bulk-schedule scope fetches; truncation disables bulk editing. */
export const SCHEDULE_WARNING_SCOPE_PAGE_SIZE = 1000;

/** Campaign-title lookup result retained for schedule pre-fill and past-event-end warnings. */
interface CampaignTitleData {
  gti: string;
  predictedStartTime?: string | null;
  predictedEndTime?: string | null;
}

const DEFAULT_PAGINATION_CONFIG = {
  page: 1,
  pageCount: 1,
  pageSize: 100,
};

export const StandaloneAssetsContainer = ({
  approvalStatus,
  onTabChange,
}: StandaloneAssetsContainerProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { createToast } = useToasterContext();
  const [currentAction, setCurrentAction] = useState<AssetStatus | undefined>(
    undefined,
  );
  const [assetApprovalModal, setAssetApprovalModal] = useState(false);
  const [bulkScheduleModalOpen, setBulkScheduleModalOpen] = useState(false);
  const [bulkScheduleGroups, setBulkScheduleGroups] = useState<
  readonly BulkScheduleGroup[]
  >([]);
  const [campaignTitleFetchCount, setCampaignTitleFetchCount] = useState(0);
  const [assetDetailApprovalModal, setAssetDetailApprovalModal] =
    useState(false);
  const [assetDetailView, setAssetDetailView] = useState(false);
  const [assetDetail, setAssetDetail] = useState<AssetDto>();

  const [selectAll, setSelectAll] = useState(
    approvalStatus === ASSET_STATUSES.PENDING,
  );
  const [selectedAssets, setSelectedAssets] = useState<AssetDto[]>([]);
  const [rejectedAssets, setRejectedAssets] = useState<AssetDto[]>([]);
  const [assetSchedules, setAssetSchedules] = useState<Map<string, { startTime: string; endTime: string }>>(new Map());
  const [assetPredictedEnd, setAssetPredictedEnd] = useState<Map<string, string>>(new Map());
  const [isUpdating, setIsUpdating] = useState(false);
  const [pagination, setPagination] = useState({
    [ASSET_STATUSES.APPROVED]: { ...DEFAULT_PAGINATION_CONFIG },
    [ASSET_STATUSES.PENDING]: { ...DEFAULT_PAGINATION_CONFIG },
    [ASSET_STATUSES.REJECTED]: { ...DEFAULT_PAGINATION_CONFIG },
  });

  // --- URL-persisted filter state ---
  // Read filters from URL params (with backward compat for `campaignId` singular)
  const urlPublisher = searchParams.get('publisher') ?? '';
  const publisher = urlPublisher || PUBLISHER_ALL;

  const urlCampaigns = searchParams.get('campaigns') ?? '';
  const legacyCampaignId = searchParams.get('campaignId') ?? '';
  const campaigns: string[] = useMemo(() => {
    if (urlCampaigns) return urlCampaigns.split(',').filter(Boolean);
    if (legacyCampaignId) return [legacyCampaignId];
    return [];
  }, [urlCampaigns, legacyCampaignId]);

  const channel = searchParams.get('channel') ?? '';
  const locale = searchParams.get('locale') ?? '';
  const country = searchParams.get('country') ?? '';
  const assetType = searchParams.get('assetType') ?? '';
  const size = searchParams.get('size') ?? '';
  const liveStatus = searchParams.get('liveStatus') ?? '';
  const businessLine = searchParams.get('businessLine') ?? '';
  const strategy = searchParams.get('strategy') ?? '';

  // Debounced text filters: local state for raw input, URL for debounced value
  const urlGti = searchParams.get('gti') ?? '';
  const urlSearch = searchParams.get('search') ?? '';
  const [gtiInput, setGtiInput] = useState(urlGti);
  const [titleSearch, setTitleSearch] = useState(urlSearch);

  // Sync local inputs when URL changes externally (e.g., deep-link navigation)
  useEffect(() => { setGtiInput(urlGti); }, [urlGti]);
  useEffect(() => { setTitleSearch(urlSearch); }, [urlSearch]);

  const [debouncedGti] = useDebounce(gtiInput, 300) as [string, boolean];
  const [debouncedTitleSearch] = useDebounce(titleSearch, 300) as [string, boolean];

  // Sync debounced GTI value to URL
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debouncedGti) {
        next.set('gti', debouncedGti);
      } else {
        next.delete('gti');
      }
      return next;
    }, { replace: true });
  }, [debouncedGti, setSearchParams]);

  // Sync debounced title search value to URL
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debouncedTitleSearch) {
        next.set('search', debouncedTitleSearch);
      } else {
        next.delete('search');
      }
      return next;
    }, { replace: true });
  }, [debouncedTitleSearch, setSearchParams]);

  // Helper to update a single URL filter param
  const setFilterParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      // Clean up legacy campaignId when writing campaigns
      if (key === 'campaigns') {
        next.delete('campaignId');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setCampaigns = useCallback((value: string[]) => {
    setFilterParam('campaigns', value.join(','));
  }, [setFilterParam]);

  const setChannel = useCallback((value: string) => {
    setFilterParam('channel', value);
  }, [setFilterParam]);

  const setLocale = useCallback((value: string) => {
    setFilterParam('locale', value);
  }, [setFilterParam]);

  const setCountry = useCallback((value: string) => {
    setFilterParam('country', value);
  }, [setFilterParam]);

  const setAssetType = useCallback((value: string) => {
    setFilterParam('assetType', value);
  }, [setFilterParam]);

  const setSize = useCallback((value: string) => {
    setFilterParam('size', value);
  }, [setFilterParam]);

  const setLiveStatus = useCallback((value: string) => {
    setFilterParam('liveStatus', value);
  }, [setFilterParam]);

  const setBusinessLine = useCallback((value: string) => {
    setFilterParam('businessLine', value);
  }, [setFilterParam]);

  const setStrategy = useCallback((value: string) => {
    setFilterParam('strategy', value);
  }, [setFilterParam]);
  const [sort, setSort] = useState<string[]>(['titles.0.name:1', 'assetType:1', 'createdAt:-1']);

  const handleSortChange = (sortColumn: string, sortDirection: SortDirection) => {
    setSortColumn(sortColumn);
    setSortDirection(sortDirection);
    const direction = sortDirection === 'ascending' ? '1' : '-1';

    // Use Name field when sorting by titles
    const selectedSortColumn = sortColumn === 'titles' ? 'titles.0.name' : sortColumn;
    setSort([`${selectedSortColumn}:${direction}`]);
  };

  const [sortColumn, setSortColumn] = useState<string>('titles');
  const [sortDirection, setSortDirection] =
    useState<SortDirection>('ascending');

  // Fetch all campaign options (id + name) the user has access to
  const { data: allCampaignOptions } = useAssetsControllerGetDistinctCampaignOptionsQuery();

  // Note: We intentionally do NOT validate URL campaign IDs against allCampaignOptions.
  // The campaigns param comes from the URL (e.g. "View Assets" button on campaign details)
  // and should be trusted. The distinct-values/campaigns endpoint may return a subset
  // or load asynchronously, causing a race condition that would incorrectly clear the filter.

  const campaignOptions = useMemo(() => {
    const options = allCampaignOptions ?? [];
    // Ensure URL-provided campaign IDs appear in the dropdown even if
    // the distinct-values endpoint hasn't returned them yet
    const missingCampaigns = campaigns.filter(
      id => id && !options.some(opt => opt.id === id),
    );
    if (missingCampaigns.length > 0) {
      const placeholders = missingCampaigns.map(id => ({ id, name: id, publisher: '', timezone: '', productStrategies: [] }));
      return [...placeholders, ...options];
    }
    return options;
  }, [allCampaignOptions, campaigns]);

  // Build campaign-like data from allCampaignOptions for child components
  const campaignsDataForDisplay = useMemo(() => {
    if (!allCampaignOptions) return [] as CampaignDesiredStateDto[];
    return allCampaignOptions.map(c => ({
      campaignId: c.id,
      name: c.name,
      publisher: c.publisher,
      productStrategies: c.productStrategies ?? [],
    })) as unknown as CampaignDesiredStateDto[];
  }, [allCampaignOptions]);

  // productStrategyId → display name, for every strategy in the in-scope campaigns.
  const strategyLabels = useMemo(
    () => buildStrategyLabelMap(campaignsDataForDisplay),
    [campaignsDataForDisplay],
  );

  // Filter dropdown options: one per strategy in the in-scope campaigns.
  const strategyOptions = useMemo(() => {
    const inScope = campaigns.length > 0
      ? (allCampaignOptions ?? []).filter(c => campaigns.includes(c.id))
      : (allCampaignOptions ?? []);
    return buildStrategyFilterOptions(
      inScope as unknown as Array<Pick<CampaignDesiredStateDto, 'productStrategies'>>,
      strategyLabels,
    );
  }, [allCampaignOptions, campaigns, strategyLabels]);

  const sportsCampaignIds = useMemo(() => {
    if (!allCampaignOptions) return new Set<string>();
    const ids = new Set<string>();
    for (const c of allCampaignOptions) {
      const isSports = (c.productStrategies as Array<{ businessLine?: string }> ?? []).some(
        (ps) => isSportsBusinessLine(ps.businessLine),
      );
      if (isSports) ids.add(c.id);
    }
    return ids;
  }, [allCampaignOptions]);

  // Build channel filter based on UI state
  const channelFilter = useMemo(() => {
    if (!channel) return undefined;
    if (channel === 'no-channel') {
      return { 'metadata.channel': { $not: { $elemMatch: { $exists: true } } } };
    }
    return { 'metadata.channel': { $in: [channel] } };
  }, [channel]);

  // Build campaign filter for multiselect
  const campaignFilter = useMemo(() => {
    if (campaigns.length > 0) {
      return { campaignIds: { $in: campaigns } };
    }
    return {};
  }, [campaigns]);

  // Build locale filter
  const localeFilter = useMemo(() => buildLocaleFilter(locale), [locale]);

  // Build asset type filter
  const assetTypeFilter = useMemo(() => buildAssetTypeFilter(assetType), [assetType]);

  // Build live status filter
  const liveStatusFilter = useMemo(() => {
    if (!liveStatus) return {};
    return { 'metadata.liveStatus': { $eq: liveStatus } };
  }, [liveStatus]);

  const assetQueryFilters = {
    approvalStatus,
    ...campaignFilter,
    ...channelFilter,
    ...localeFilter,
    ...assetTypeFilter,
    ...liveStatusFilter,
    ...(debouncedGti && { 'titles.gti': debouncedGti }),
    ...(debouncedTitleSearch && { 'titles.name': { $regex: debouncedTitleSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), $options: 'i' } }),
  };

  // Use /assets endpoint — publisher and country are resolved server-side
  const {
    data: programAssets,
    isFetching,
    isLoading,
  } = useAssetsControllerFindManyQuery({
    filters: assetQueryFilters,
    ...(publisher !== PUBLISHER_ALL && { publisher }),
    ...(country && { country }),
    ...(businessLine && { businessLine: businessLine as BusinessLine }),
    ...(size && { size }),
    ...(strategy && { productStrategyId: strategy }),
    page: pagination[approvalStatus].page,
    pageSize: pagination[approvalStatus].pageSize,
    sort,
    // Group by (assetId, productStrategyId) → one card/row per strategy.
    groupByAssetId: true,
  });

  // Cross-page schedule-warning scope: overlap and coverage-gap are cross-asset properties, so
  // warnings must consider ALL pending assets in sports campaigns — not just the current page.
  // Fetched unfiltered (aside from status + sports campaigns) so user filters/pagination don't
  // hide a conflicting asset. Bounded by SCHEDULE_WARNING_SCOPE_PAGE_SIZE; beyond that the scope
  // degrades to a partial (but still page-superset) view.
  const sportsCampaignIdList = useMemo(
    () => Array.from(sportsCampaignIds).sort(),
    [sportsCampaignIds],
  );

  const {
    data: pendingSportsAssets,
    isError: isScheduleScopeError,
  } = useAssetsControllerFindManyQuery(
    {
      filters: {
        approvalStatus: ASSET_STATUSES.PENDING,
        campaignIds: { $in: sportsCampaignIdList },
      },
      page: 1,
      pageSize: SCHEDULE_WARNING_SCOPE_PAGE_SIZE,
      // Deterministic order so, if the result set exceeds the bound, the retained subset is
      // stable across refetches instead of depending on backend default ordering.
      sort: ['createdAt:-1', 'assetId:1'],
      groupByAssetId: true,
    },
    {
      skip:
        approvalStatus !== ASSET_STATUSES.PENDING ||
        sportsCampaignIdList.length === 0,
    },
  );

  const bulkScheduleCampaignIds = useMemo(
    () => campaigns.length > 0
      ? sportsCampaignIdList.filter((campaignId) => campaigns.includes(campaignId))
      : sportsCampaignIdList,
    [campaigns, sportsCampaignIdList],
  );

  // The bulk editor has a distinct cross-page scope because, unlike warnings, it must honor
  // every active user filter. Reusing the server query preserves filter semantics that cannot be
  // reproduced reliably from asset DTOs (publisher, country, business line, and strategy).
  const {
    currentData: pendingFilteredSportsAssets,
    isError: isBulkScheduleScopeError,
    isFetching: isBulkScheduleScopeFetching,
  } = useAssetsControllerFindManyQuery(
    {
      filters: {
        ...assetQueryFilters,
        approvalStatus: ASSET_STATUSES.PENDING,
        campaignIds: { $in: bulkScheduleCampaignIds },
      },
      ...(publisher !== PUBLISHER_ALL && { publisher }),
      ...(country && { country }),
      ...(businessLine && { businessLine: businessLine as BusinessLine }),
      ...(size && { size }),
      ...(strategy && { productStrategyId: strategy }),
      page: 1,
      pageSize: SCHEDULE_WARNING_SCOPE_PAGE_SIZE,
      sort: ['createdAt:-1', 'assetId:1'],
      groupByAssetId: true,
    },
    {
      skip:
        approvalStatus !== ASSET_STATUSES.PENDING ||
        bulkScheduleCampaignIds.length === 0,
    },
  );

  const bulkScheduleScopeAssets = useMemo(
    () => normalizeAssetsCampaignIds(pendingFilteredSportsAssets?.data ?? []),
    [pendingFilteredSportsAssets?.data],
  );

  const isBulkScheduleScopeTruncated =
    (pendingFilteredSportsAssets?.meta?.pagination?.total ?? 0) >
    SCHEDULE_WARNING_SCOPE_PAGE_SIZE;

  // The warning scope degrades (page-only or truncated) on scope-query failure or overflow.
  // Warnings stay advisory either way, but the degradation should not be silent.
  useEffect(() => {
    const total = pendingSportsAssets?.meta?.pagination?.total;
    if (typeof total === 'number' && total > SCHEDULE_WARNING_SCOPE_PAGE_SIZE) {
      Logger.warn('Schedule-warning scope truncated; warnings may be incomplete', {
        total,
        bound: SCHEDULE_WARNING_SCOPE_PAGE_SIZE,
      });
    }
  }, [pendingSportsAssets]);

  useEffect(() => {
    if (isScheduleScopeError) {
      Logger.error('Failed to fetch schedule-warning scope; warnings limited to the current page');
    }
  }, [isScheduleScopeError]);

  useEffect(() => {
    const total = pendingFilteredSportsAssets?.meta?.pagination?.total;
    if (typeof total === 'number' && total > SCHEDULE_WARNING_SCOPE_PAGE_SIZE) {
      Logger.warn('Bulk-schedule scope truncated; bulk editing is unavailable', {
        total,
        bound: SCHEDULE_WARNING_SCOPE_PAGE_SIZE,
      });
    }
  }, [pendingFilteredSportsAssets]);

  useEffect(() => {
    if (isBulkScheduleScopeError) {
      Logger.error('Failed to fetch bulk-schedule scope; bulk editing is unavailable');
    }
  }, [isBulkScheduleScopeError]);

  // Use distinct-values endpoint without programId for channel options
  const {
    data: availableChannels,
    isFetching: isChannelsFetching,
  } = useDistinctValuesControllerGetDistinctValuesQuery({
    entityType: 'asset',
    field: 'metadata.channel',
    filters: {
      ...(approvalStatus && { approvalStatus }),
      ...(campaigns.length > 0 && { campaignIds: campaigns }),
    },
  });

  // Fetch distinct asset sizes (e.g. "300x600", "728x90")
  const {
    data: availableSizes,
    isFetching: isSizesFetching,
  } = useDistinctValuesControllerGetDistinctValuesQuery({
    entityType: 'asset',
    field: 'metadata.size',
    filters: {
      ...(approvalStatus && { approvalStatus }),
      ...(campaigns.length > 0 && { campaignIds: campaigns }),
    },
  });

  // Fetch distinct live statuses (e.g. "COMING_SOON", "LIVE_NOW"). The Live Status
  // filter only renders when at least one asset in the response has this field set.
  const {
    data: availableLiveStatuses,
  } = useDistinctValuesControllerGetDistinctValuesQuery({
    entityType: 'asset',
    field: 'metadata.liveStatus',
    filters: {
      ...(approvalStatus && { approvalStatus }),
      ...(campaigns.length > 0 && { campaignIds: campaigns }),
    },
  });

  const availablePublishers = useAvailablePublishers();
  const publishers = useMemo(
    () => [PUBLISHER_ALL, ...availablePublishers],
    [availablePublishers],
  );

  const handleClearFilters = () => {
    setGtiInput('');
    setTitleSearch('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Preserve tab param, clear everything else
      const tab = next.get('tab');
      const cleared = new URLSearchParams();
      if (tab) cleared.set('tab', tab);
      return cleared;
    }, { replace: true });
  };

  const [updateAssets] = useAssetsControllerUpdateMutation();

  const paginationResponse = useMemo(
    () => programAssets?.meta.pagination,
    [programAssets?.meta.pagination],
  );

  useEffect(() => {
    setAssetDetailView(false);
  }, [approvalStatus]);

  const dataTestIdSuffix = useMemo(
    () => approvalStatus.toLowerCase(),
    [approvalStatus],
  );

  const viableAssets = useMemo(() => {
    if (programAssets?.data) {
      return normalizeAssetsCampaignIds(programAssets.data);
    }
    return [];
  }, [programAssets?.data]);

  const assetListGtis = useMemo(() => {
    const distinctGtis = new Set<string>();
    viableAssets.forEach((asset) => {
      asset.titles.forEach((title) => distinctGtis.add(title.gti));
    });
    return Array.from(distinctGtis);
  }, [viableAssets]);

  const {
    isFetching: isAssetListTitlesFetching,
    releaseDatesByGti,
  } = useCampaignTitleReleaseDates({
    gtis: assetListGtis,
    ...(country && {
      countryCode:
        country as CampaignTitlesControllerGetCampaignTitlesApiArg['countryCode'],
    }),
    ...(locale && {
      locale:
        locale as CampaignTitlesControllerGetCampaignTitlesApiArg['locale'],
    }),
  });

  const titleReleaseDates = useMemo<ReadonlyArray<{
    name?: string;
    releaseDate?: string;
  }>>(() => (assetDetail?.titles ?? []).map((title) => ({
    name: title.name,
    releaseDate: releaseDatesByGti.get(title.gti),
  })), [assetDetail?.titles, releaseDatesByGti]);

  useEffect(() => {
    if (!assetDetailView) return;

    const matchingAsset = assetDetail && viableAssets.find((asset) =>
      isSameAssetStrategy(asset, assetDetail));
    if (matchingAsset) {
      setAssetDetail(matchingAsset);
    } else {
      // Asset was removed (approved/rejected) — advance to the asset at the same position
      // (clamped to the end of the list) rather than jumping back to the first.
      const clampedIndex = Math.min(lastDetailIndexRef.current, viableAssets.length - 1);
      setAssetDetail(viableAssets[Math.max(0, clampedIndex)]);
    }
  }, [assetDetail, assetDetailView, viableAssets]);

  const assetDetailIndex = assetDetail
    ? viableAssets.findIndex((asset) => isSameAssetStrategy(asset, assetDetail))
    : -1;

  // Keep a ref of the current index so it survives across refetches
  const lastDetailIndexRef = useRef(0);
  useEffect(() => {
    if (assetDetailIndex >= 0) lastDetailIndexRef.current = assetDetailIndex;
  }, [assetDetailIndex]);

  const handleAssetDetailNavigation = useCallback((offset: number): void => {
    const nextAsset = viableAssets[assetDetailIndex + offset];
    if (nextAsset) setAssetDetail(nextAsset);
  }, [assetDetailIndex, viableAssets]);

  // Keyboard navigation: left/right arrow keys move between assets in detail mode
  useEffect(() => {
    if (!assetDetailView) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handleAssetDetailNavigation(-1);
      } else if (e.key === 'ArrowRight') {
        handleAssetDetailNavigation(1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [assetDetailView, handleAssetDetailNavigation]);

  // Union (by assetStrategyKey) of the current page and the cross-page pending sports assets.
  // Schedule pre-fill and warning computation both run over this full set so a row on another page
  // still participates in overlap / coverage-gap detection for rows on this page.
  const scheduleScopeAssets = useMemo(() => {
    const byRow = new Map<string, AssetDto>();
    for (const asset of viableAssets) {
      byRow.set(assetStrategyKey(asset), asset);
    }
    for (const asset of normalizeAssetsCampaignIds(pendingSportsAssets?.data ?? [])) {
      const key = assetStrategyKey(asset);
      if (!byRow.has(key)) {
        byRow.set(key, asset);
      }
    }
    return Array.from(byRow.values());
  }, [viableAssets, pendingSportsAssets?.data]);

  // A complete filtered modal scope can include assets beyond a truncated warning scope. Include
  // both when pre-filling schedules and event data, while warning computation stays unchanged.
  const schedulePrefillScopeAssets = useMemo(() => {
    const byRow = new Map<string, AssetDto>();
    for (const asset of scheduleScopeAssets) {
      byRow.set(assetStrategyKey(asset), asset);
    }
    for (const asset of bulkScheduleScopeAssets) {
      byRow.set(assetStrategyKey(asset), asset);
    }
    return Array.from(byRow.values());
  }, [bulkScheduleScopeAssets, scheduleScopeAssets]);

  const rowCount = viableAssets.length;

  // The evaluation badge only needs the rules actually evaluated on the displayed
  // assets. Collect those rule ids and fetch just their configs (name + severity)
  // rather than every content rule.
  const evaluatedRuleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const asset of viableAssets) {
      for (const verdict of asset.evaluatedRules ?? []) {
        ids.add(verdict.ruleId);
      }
    }
    return Array.from(ids);
  }, [viableAssets]);

  const contentRuleQueryArg: CampaignConfigurationControllerFindManyApiArg = {
    configType: ConfigurationType.CONTENT_RULE,
    filters: { _id: { $in: evaluatedRuleIds } },
    pageSize: evaluatedRuleIds.length,
    page: 1,
    includeCampaignCount: false,
  };

  const { data: contentRuleConfigs } = useCampaignConfigurationControllerFindManyQuery(
    contentRuleQueryArg,
    { skip: approvalStatus !== ASSET_STATUSES.PENDING || evaluatedRuleIds.length === 0 },
  );

  const contentRuleMap = useMemo<RuleMap>(() => {
    const map: RuleMap = new Map();
    for (const config of (contentRuleConfigs?.data ?? []) as TypedCampaignConfigurationDto[]) {
      const value = config.config;
      if (value.configType === ConfigurationType.CONTENT_RULE) {
        const { severity } = value as ContentRuleConfigValue;
        map.set(config.id, { name: config.name ?? config.id, severity });
      }
    }
    return map;
  }, [contentRuleConfigs?.data]);

  // Keyed per (asset × strategy) via assetStrategyKey.
  const handleAssetScheduleChange = useCallback((key: string, schedule: { startTime: string; endTime: string }) => {
    setAssetSchedules((prev) => {
      const next = new Map(prev);
      next.set(key, schedule);
      return next;
    });
  }, []);

  const [fetchCampaignTitles] = useCampaignTitlesControllerGetCampaignTitlesBatchMutation();

  interface CampaignScheduleInfo {
    scheduleWindows: Record<string, TemplateScheduleWindow>;
    businessLine: string;
    templateConfigId?: string;
  }

  // Publisher account timezone per campaign — sports asset schedules are computed in this zone.
  const campaignTimezoneMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of allCampaignOptions ?? []) {
      if (c.timezone) map.set(c.id, c.timezone);
    }
    return map;
  }, [allCampaignOptions]);

  const sportsCampaignScheduleMap = useMemo(() => {
    const map = new Map<string, CampaignScheduleInfo[]>();
    if (!allCampaignOptions) return map;

    for (const c of allCampaignOptions) {
      const strategies = c.productStrategies as Array<{ businessLine?: string; country?: string; benderTemplates?: Array<{ templateConfigId?: string; scheduleWindows?: CampaignScheduleInfo['scheduleWindows'] }> }> ?? [];
      const infos: CampaignScheduleInfo[] = [];
      for (const ps of strategies) {
        if (!isSportsBusinessLine(ps.businessLine)) continue;

        for (const t of ps.benderTemplates ?? []) {
          if (t.scheduleWindows && Object.keys(t.scheduleWindows).length > 0) {
            infos.push({
              scheduleWindows: t.scheduleWindows,
              businessLine: ps.businessLine!,
              templateConfigId: t.templateConfigId,
            });
          }
        }
      }
      if (infos.length > 0) {
        map.set(c.id, infos);
      }
    }
    return map;
  }, [allCampaignOptions]);

  // Cache of campaign-title lookups keyed by `${businessLine}::${gti}` (null = fetched, no data).
  // The pre-fill effect re-runs whenever `schedulePrefillScopeAssets` changes identity (pagination,
  // filters, approval refetches); this cache ensures each GTI is fetched at most once per mount —
  // only the un-fetched delta goes over the network on subsequent runs.
  const titleCacheRef = useRef<Map<string, CampaignTitleData | null>>(new Map());

  const hasBulkScheduleEligibleAssets = useMemo(() => {
    if (bulkScheduleCampaignIds.length === 0) return false;

    const isEligible = (asset: AssetDto): boolean =>
      isBulkScheduleEligibleAsset(asset, sportsCampaignScheduleMap);

    if (bulkScheduleScopeAssets.some(isEligible)) return true;
    return (isBulkScheduleScopeFetching || isBulkScheduleScopeError) &&
      viableAssets.some(isEligible);
  }, [
    bulkScheduleCampaignIds.length,
    bulkScheduleScopeAssets,
    isBulkScheduleScopeError,
    isBulkScheduleScopeFetching,
    sportsCampaignScheduleMap,
    viableAssets,
  ]);

  const handleBulkScheduleOpen = useCallback((): void => {
    const eventInfoByGti = new Map<string, BulkScheduleEventInfo>();
    titleCacheRef.current.forEach((title: CampaignTitleData | null) => {
      if (title === null) return;
      eventInfoByGti.set(title.gti, {
        predictedStartTime: title.predictedStartTime,
        predictedEndTime: title.predictedEndTime,
      });
    });

    setBulkScheduleGroups(groupAssetsForBulkSchedule(
      bulkScheduleScopeAssets,
      sportsCampaignScheduleMap,
      assetSchedules,
      eventInfoByGti,
    ));
    setBulkScheduleModalOpen(true);
  }, [assetSchedules, bulkScheduleScopeAssets, sportsCampaignScheduleMap]);

  const handleBulkScheduleApply = useCallback(
    (dirty: Map<string, Schedule>): void => {
      setAssetSchedules((previous) => {
        const next = new Map(previous);
        dirty.forEach((schedule, groupId) => {
          const group = bulkScheduleGroups.find(
            (candidate) => candidate.groupId === groupId,
          );
          group?.assetKeys.forEach((key) => { next.set(key, schedule); });
        });
        return next;
      });
    },
    [bulkScheduleGroups],
  );

  const bulkScheduleAction = useMemo(
    () => approvalStatus === ASSET_STATUSES.PENDING && hasBulkScheduleEligibleAssets
      ? {
          disabled:
            isBulkScheduleScopeFetching ||
            isBulkScheduleScopeError ||
            isBulkScheduleScopeTruncated ||
            campaignTitleFetchCount > 0,
          onClick: handleBulkScheduleOpen,
        }
      : undefined,
    [
      approvalStatus,
      campaignTitleFetchCount,
      handleBulkScheduleOpen,
      hasBulkScheduleEligibleAssets,
      isBulkScheduleScopeError,
      isBulkScheduleScopeFetching,
      isBulkScheduleScopeTruncated,
    ],
  );

  useEffect(() => {
    if (approvalStatus !== ASSET_STATUSES.PENDING || !schedulePrefillScopeAssets.length || sportsCampaignScheduleMap.size === 0) return;

    const sportsAssets = schedulePrefillScopeAssets.filter((a) => a.campaignId && sportsCampaignScheduleMap.has(a.campaignId));
    if (sportsAssets.length === 0) return;

    const titleCache = titleCacheRef.current;

    const gtisByBusinessLine = new Map<string, Set<string>>();
    for (const asset of sportsAssets) {
      const campaignInfos = sportsCampaignScheduleMap.get(asset.campaignId!);
      const bl = campaignInfos?.[0]?.businessLine;
      if (!bl) continue;
      for (const title of asset.titles) {
        const existing = gtisByBusinessLine.get(bl) ?? new Set<string>();
        existing.add(title.gti);
        gtisByBusinessLine.set(bl, existing);
      }
    }
    if (gtisByBusinessLine.size === 0) return;

    // Fetch only GTIs not already cached.
    const toFetch = Array.from(gtisByBusinessLine.entries())
      .map(([bl, gtis]) => [bl, Array.from(gtis).filter((gti) => !titleCache.has(`${bl}::${gti}`))] as const)
      .filter(([, gtis]) => gtis.length > 0);

    Promise.all(
      toFetch.map(([bl, gtis]) => {
        setCampaignTitleFetchCount((count) => count + 1);
        return fetchCampaignTitles({ getCampaignTitlesRequest: { gtiList: gtis, businessLine: bl as BusinessLine } })
          .unwrap()
          .then((result) => {
            const titles = result as CampaignTitleData[];
            for (const title of titles) titleCache.set(`${bl}::${title.gti}`, title);
            // Mark misses too, so a GTI with no title data isn't re-requested on every run.
            const returned = new Set(titles.map((t) => t.gti));
            for (const gti of gtis) {
              if (!returned.has(gti)) titleCache.set(`${bl}::${gti}`, null);
            }
          })
          .finally(() => {
            setCampaignTitleFetchCount((count) => Math.max(0, count - 1));
          });
      }),
    )
      .then(() => {
        const newSchedules = new Map<string, { startTime: string; endTime: string }>();
        const newPredictedEnd = new Map<string, string>();

        for (const asset of sportsAssets) {
          if (asset.titles.length !== 1) continue;

          const key = assetStrategyKey(asset);
          const campaignInfos = sportsCampaignScheduleMap.get(asset.campaignId!);
          const bl = campaignInfos?.[0]?.businessLine;
          const gti = asset.titles[0]?.gti;
          const title = gti && bl ? titleCache.get(`${bl}::${gti}`) : undefined;

          // Keep predictedEndTime per row for the past-event-end warning, even without a schedule.
          if (title?.predictedEndTime) {
            newPredictedEnd.set(key, title.predictedEndTime);
          }

          if (assetSchedules.has(key)) continue;
          if (!campaignInfos?.length) continue;

          const templateConfigId = asset.templateConfigIds?.[0];
          const matched = campaignInfos.find((i) => i.templateConfigId === templateConfigId) ?? campaignInfos[0];

          if (!title?.predictedStartTime) continue;

          // Apply the schedule window for this asset's specific live-status message variant.
          const liveStatus = asset.metadata?.liveStatus;
          const window = liveStatus ? matched.scheduleWindows[liveStatus] : undefined;
          if (!window) continue;

          // Compute the schedule in the campaign's publisher-account timezone. Skip when the
          // config has no timezone (defensive — every publisher config requires one).
          const publisherTimezone = campaignTimezoneMap.get(asset.campaignId!);
          if (!publisherTimezone) continue;

          const computed = computeAssetSchedule(window, title.predictedStartTime, publisherTimezone, title.predictedEndTime);
          if (computed) {
            newSchedules.set(key, computed);
          }
        }

        // Both setters return `prev` unchanged when nothing is new, so repeated effect runs over
        // an already-processed scope don't trigger re-renders.
        if (newSchedules.size > 0) {
          setAssetSchedules((prev) => {
            let changed = false;
            const next = new Map(prev);
            newSchedules.forEach((sched, id) => {
              if (!next.has(id)) {
                next.set(id, sched);
                changed = true;
              }
            });
            return changed ? next : prev;
          });
        }

        if (newPredictedEnd.size > 0) {
          setAssetPredictedEnd((prev) => {
            let changed = false;
            const next = new Map(prev);
            newPredictedEnd.forEach((end, id) => {
              if (next.get(id) !== end) {
                next.set(id, end);
                changed = true;
              }
            });
            return changed ? next : prev;
          });
        }
      })
      .catch((err) => {
        Logger.error('Failed to pre-fill asset schedules', { err });
      });
    // Intentionally omits assetSchedules (would cause infinite loop) and fetchCampaignTitles (stable RTK mutation ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus, schedulePrefillScopeAssets, sportsCampaignScheduleMap, campaignTimezoneMap]);

  // Non-blocking schedule warnings (overlap / past-event-end / coverage-gap), scoped per
  // campaign + title. Computed over the full cross-page scope so off-page assets are considered.
  const scheduleWarnings = useMemo(
    () => computeScheduleWarnings(scheduleScopeAssets, assetSchedules, assetPredictedEnd),
    [scheduleScopeAssets, assetSchedules, assetPredictedEnd],
  );

  const handleAssetSelectV2 = (inputAsset: AssetDto) => {
    if (selectedAssets.some((asset) => isSameAssetStrategy(asset, inputAsset))) {
      setSelectedAssets(
        selectedAssets.filter((asset) => !isSameAssetStrategy(asset, inputAsset)),
      );
      setRejectedAssets([...rejectedAssets, inputAsset]);
    } else {
      setSelectedAssets([...selectedAssets, inputAsset]);
      setRejectedAssets(
        rejectedAssets.filter((asset) => !isSameAssetStrategy(asset, inputAsset)),
      );
    }
  };

  const handleAssetSelect = (inputAsset: AssetDto) => {
    if (selectedAssets.some((asset) => isSameAssetStrategy(asset, inputAsset))) {
      setSelectedAssets(
        selectedAssets.filter((asset) => !isSameAssetStrategy(asset, inputAsset)),
      );
    } else {
      setSelectedAssets([...selectedAssets, inputAsset]);
    }
  };

  const toggleSelectAll = () => {
    setSelectAll((prev) => !prev);
  };

  // Use asset.programId from each asset instead of a single program.programId
  const handleStatusUpdateV2 = async (
    action: AssetStatus | undefined,
  ): Promise<void> => {
    if (!action) {
      return;
    }

    if (action === ASSET_STATUSES.APPROVED) {
      const missingSchedule = selectedAssets.filter((asset) => {
        const hasScheduleWindow = asset.campaignId && sportsCampaignScheduleMap.has(asset.campaignId);
        if (!hasScheduleWindow) return false;
        const schedule = assetSchedules.get(assetStrategyKey(asset));
        return !schedule?.startTime || !schedule?.endTime;
      });

      if (missingSchedule.length > 0) {
        createToast({
          type: AlertType.ERROR,
          message: t('asset_schedule_required_for_sports'),
        });
        return;
      }
    }

    // One update entry per row, carrying its own productStrategyId.
    interface UpdateEntry {
      assetId: string;
      programId: string;
      approvalStatus: AssetStatus;
      productStrategyId?: string;
      schedule?: { startTime: string; endTime: string };
    }
    const buildEntry = (asset: AssetDto, status: AssetStatus): UpdateEntry => {
      const schedule = assetSchedules.get(assetStrategyKey(asset));
      // A schedule is only meaningful on an approved row — never persist one on a reject.
      const scheduleFields = status !== ASSET_STATUSES.REJECTED && schedule?.startTime && schedule?.endTime
        ? { schedule }
        : {};
      return {
        assetId: asset.assetId,
        programId: asset.programId ?? '',
        ...scheduleFields,
        ...(asset.productStrategyId ? { productStrategyId: asset.productStrategyId } : {}),
        approvalStatus: status,
      };
    };

    // Build all entries, then group by their resolved status (APPROVED / REJECTED / other).
    const allEntries = [
      ...selectedAssets.map((asset) => buildEntry(asset, action)),
      ...rejectedAssets.map((asset) => buildEntry(asset, ASSET_STATUSES.REJECTED as AssetStatus)),
    ];
    const approvedEntries = allEntries.filter(e => e.approvalStatus === ASSET_STATUSES.APPROVED);
    const rejectedEntries = allEntries.filter(e => e.approvalStatus === ASSET_STATUSES.REJECTED);
    const otherEntries = allEntries.filter(
      e => e.approvalStatus !== ASSET_STATUSES.APPROVED && e.approvalStatus !== ASSET_STATUSES.REJECTED,
    );

    setIsUpdating(true);

    // Send one status group as chunked batches; returns false if any batch threw.
    const sendGroup = async (entries: typeof allEntries, failureMessage: string): Promise<boolean> => {
      if (entries.length === 0) return true;
      const batches = chunk(entries, ASSET_UPDATE_BATCH_SIZE);
      try {
        await Promise.all(
          batches.map(async (batchAssets, index) => {
            const response = await updateAssets({ updateAssetsDto: { assets: batchAssets } });
            const messagePrefix = t('asset_batch_of_total', { number: index + 1, total: batches.length }) + ': ';
            handleUpdateResponse(response, batches.length > 1 ? messagePrefix : '');
          }),
        );
        return true;
      } catch (e) {
        createToast({ type: AlertType.ERROR, message: failureMessage });
        Logger.error('Error updating asset statuses.', { assets: entries, error: e as Error });
        return false;
      }
    };

    // Approved first (bail on failure), then rejected, then any other target.
    const approvedOk = await sendGroup(approvedEntries, t('approved_assets_update_failure'));
    if (!approvedOk) {
      setIsUpdating(false);
      setAssetApprovalModal(false);
      return;
    }
    await sendGroup(rejectedEntries, t('rejected_assets_update_failure'));
    await sendGroup(otherEntries, t('asset_update_failure'));

    setIsUpdating(false);
    setAssetApprovalModal(false);
  };

  const handleSingleStatusUpdate = async (
    action: AssetStatus | undefined,
    asset: AssetDto,
  ): Promise<void> => {
    if (!action) {
      return;
    }
    setIsUpdating(true);

    try {
      const response = await updateAssets({
        updateAssetsDto: {
          assets: [{
            assetId: asset.assetId,
            programId: asset.programId ?? '',
            approvalStatus: action,
            // Scope to this strategy's row when the asset carries one.
            ...(asset.productStrategyId ? { productStrategyId: asset.productStrategyId } : {}),
          }],
        },
      });
      handleUpdateResponse(response, '');
    } catch (e) {
      createToast({
        type: AlertType.ERROR,
        message: t('asset_update_failure'),
      });

      const error = e as Error;
      Logger.error('Error updating asset approval status', {
        approvalStatus: action,
        asset,
        error,
      });
    }
    setIsUpdating(false);
    setAssetDetailApprovalModal(false);
    // Stay in detail view — the approved/rejected asset will disappear from viableAssets
    // on refetch and the next asset in the list will be shown automatically.
  };

  const handleActionClick = async (action: AssetStatus): Promise<void> => {
    setCurrentAction(action);
    setAssetApprovalModal(true);
  };

  const handleActionClickAssetDetail = async (action: AssetStatus): Promise<void> => {
    setCurrentAction(action);
    setAssetDetailApprovalModal(true);
  };

  const handleUpdateResponse = (
    response: { data: AssetsControllerUpdateApiResponse } | { error: unknown },
    messagePrefix: string = '',
  ) => {
    if ('data' in response) {
      createToast({
        type: AlertType.SUCCESS,
        message: messagePrefix + t('asset_update_success'),
      });
    } else {
      createToast({
        type: AlertType.ERROR,
        message: messagePrefix + t('asset_update_failure'),
      });
    }
  };

  useEffect(() => {
    if (!(isLoading || isFetching) && viableAssets) {
      if (selectAll) {
        if (approvalStatus === ASSET_STATUSES.PENDING) {
          // Pre-select the reject radio for assets flagged by evaluation (a VIOLATED rule that is
          // not INFO): they go into the rejected list and everything else stays approve-by-default.
          // This only stages the selection — nothing is submitted until the reviewer confirms.
          const flagged = viableAssets.filter((a) => isAssetFlagged(a.evaluatedRules));
          const flaggedSet = new Set(flagged);
          setRejectedAssets(flagged);
          setSelectedAssets(viableAssets.filter((a) => !flaggedSet.has(a)));
        } else {
          setSelectedAssets([...viableAssets]);
        }
      } else {
        setSelectedAssets([]);
        if (approvalStatus === ASSET_STATUSES.PENDING) {
          setRejectedAssets([...viableAssets]);
        }
      }
    }
  }, [approvalStatus, isFetching, isLoading, viableAssets, selectAll]);

  useEffect(() => {
    setSelectAll(approvalStatus === ASSET_STATUSES.PENDING);
    setRejectedAssets([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus, paginationResponse?.page]);

  useEffect(() => {
    setPagination((prev) => {
      let page = paginationResponse?.page;
      const pageCount = paginationResponse?.pageCount;
      if (page && pageCount && page > pageCount) page = pageCount;

      return {
        ...prev,
        [approvalStatus]: {
          ...prev[approvalStatus],
          page: page ?? 1,
          pageCount: pageCount ?? 1,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationResponse]);

  useEffect(() => {
    // Reset pagination to page 1 when filters change
    setPagination(prev => ({
      ...prev,
      [approvalStatus]: {
        ...prev[approvalStatus],
        page: 1,
      },
    }));
  }, [campaigns, publisher, channel, size, locale, country, assetType, liveStatus, businessLine, strategy, debouncedGti, debouncedTitleSearch, approvalStatus]);

  return (
    <div data-testid={`assets-${dataTestIdSuffix}`}>
      <Column>
        <AssetsActionBar
              actionDisabled={assetDetailView
                ? !assetDetail
                : selectedAssets.length === 0 && rejectedAssets.length === 0}
              approvalStatus={assetDetailView && assetDetail
                ? assetDetail.approvalStatus as AssetStatus
                : approvalStatus}
              assetType={assetType}
              availableChannels={(availableChannels ?? []) as unknown as string[]}
              availableLiveStatuses={(availableLiveStatuses ?? []) as unknown as string[]}
              availableSizes={(availableSizes ?? []) as unknown as string[]}
              bulkScheduleAction={bulkScheduleAction}
              businessLine={businessLine}
              campaignOptions={campaignOptions}
              campaigns={campaigns}
              channel={channel}
              country={country}
              gti={gtiInput}
              hasAssets={viableAssets.length !== 0}
              isChannelsFetching={isChannelsFetching}
              isDetailView={assetDetailView}
              isSizesFetching={isSizesFetching}
              liveStatus={liveStatus}
              locale={locale}
              publisher={publisher}
              publishers={publishers}
              selectAll={selectAll}
              selectedCount={selectedAssets.length + rejectedAssets.length}
              showSelectionControls={!assetDetailView}
              size={size}
              strategy={strategy}
              strategyOptions={strategyOptions}
              titleSearch={titleSearch}
              onActionClick={assetDetailView ? handleActionClickAssetDetail : handleActionClick}
              onAssetTypeChange={setAssetType}
              onBusinessLineChange={setBusinessLine}
              onCampaignsChange={(value: string[]) => { setCampaigns(value); }}
              onChannelChange={setChannel}
              onClearFilters={handleClearFilters}
              onCountryChange={setCountry}
              onGtiChange={setGtiInput}
              onLiveStatusChange={setLiveStatus}
              onLocaleChange={setLocale}
              onPublisherChange={(value: string) => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (value && value !== PUBLISHER_ALL) {
                    next.set('publisher', value);
                  } else {
                    next.delete('publisher');
                  }
                  // Clear dependent filters when publisher changes
                  next.delete('campaigns');
                  next.delete('campaignId');
                  next.delete('channel');
                  next.delete('size');
                  next.delete('locale');
                  next.delete('country');
                  next.delete('assetType');
                  next.delete('liveStatus');
                  next.delete('businessLine');
                  next.delete('strategy');
                  return next;
                }, { replace: true });
              }}
              onSizeChange={setSize}
              onStrategyChange={setStrategy}
              onTitleSearchChange={setTitleSearch}
              onToggleDetailView={() => { setAssetDetailView(!assetDetailView); }}
              onToggleSelectAll={toggleSelectAll}
            />
            <TabGroup fill="line" value={approvalStatus} onChange={onTabChange}>
              <Tab data-testid="assets-tab-pending" value={ASSET_STATUSES.PENDING}>
                {t('asset_status_pending')}
              </Tab>
              <Tab data-testid="assets-tab-approved" value={ASSET_STATUSES.APPROVED}>
                {t('asset_status_approved')}
              </Tab>
              <Tab data-testid="assets-tab-rejected" value={ASSET_STATUSES.REJECTED}>
                {t('asset_status_rejected')}
              </Tab>
            </TabGroup>
            <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="300">
              <Row alignmentVertical="center" spacing="300">
                <ShowingCount
                  i18nKey="assets_showing_count"
                  page={paginationResponse?.page ?? 1}
                  pageSize={paginationResponse?.pageSize ?? pagination[approvalStatus].pageSize}
                  total={paginationResponse?.total ?? rowCount}
                />
                {isFetching && !isLoading && (
                  <Loader size="small" type="linear" />
                )}
              </Row>
              {assetDetailView && assetDetail && (
                <Row alignmentVertical="center" spacing="200">
                  <Button
                    aria-label={t('asset_detail_previous')}
                    data-testid="asset-detail-previous"
                    disabled={assetDetailIndex <= 0}
                    type="icon"
                    onClick={() => { handleAssetDetailNavigation(-1); }}
                  >
                    <Icon tokens={chevronLeftSmallTokens}>{t('asset_detail_previous')}</Icon>
                  </Button>
                  <Text data-testid="asset-detail-position" type="b300">
                    {t('asset_detail_position', {
                      current: assetDetailIndex + 1,
                      total: viableAssets.length,
                    })}
                  </Text>
                  <Button
                    aria-label={t('asset_detail_next')}
                    data-testid="asset-detail-next"
                    disabled={assetDetailIndex >= viableAssets.length - 1}
                    type="icon"
                    onClick={() => { handleAssetDetailNavigation(1); }}
                  >
                    <Icon tokens={chevronRightSmallTokens}>{t('asset_detail_next')}</Icon>
                  </Button>
                </Row>
              )}
            </Row>
            {assetDetailView && assetDetail
              ? (
              <AssetDetailView
                  asset={assetDetail}
                  campaigns={campaignsDataForDisplay}
                  isTitleReleaseDatesLoading={isAssetListTitlesFetching}
                  programId={assetDetail.programId ?? ''}
                  titleReleaseDates={titleReleaseDates}
                />
                )
              : (
              <>
                <Box
                  backgroundColor={approvalStatus === ASSET_STATUSES.PENDING ? '#E9EBEF' : undefined}
                  type={approvalStatus === ASSET_STATUSES.PENDING ? 'outline' : undefined}
                >
                {(isLoading || (isFetching && viableAssets.length === 0)) && (
                  <Column
                    alignmentHorizontal="center"
                    alignmentVertical="center"
                    height="300px"
                  >
                    <PageLoader message={t('asset_loading')} />
                  </Column>
                )}
                {!(isLoading || (isFetching && viableAssets.length === 0)) &&
                    viableAssets.length === 0 && (
                    <Row
                      alignmentHorizontal="center"
                      alignmentVertical="center"
                      height="300px"
                    >
                      <Row>
                        <Icon tokens={infoKnockoutTokens} />
                        <Text type="h100">
                          {t('no_assets', {
                            assetStatus: `${approvalStatus.toLowerCase()}`,
                          })}
                        </Text>
                      </Row>
                    </Row>
                )}
                {!(isLoading || (isFetching && viableAssets.length === 0)) &&
                    viableAssets.length !== 0 &&
                  approvalStatus === ASSET_STATUSES.PENDING && (
                    <AssetPendingView
                      action={handleAssetSelectV2}
                      campaigns={campaignsDataForDisplay}
                      isReleaseDatesFetching={isAssetListTitlesFetching}
                      programAssets={viableAssets}
                      programId=""
                      releaseDatesByGti={releaseDatesByGti}
                      ruleMap={contentRuleMap}
                      scheduleWarnings={scheduleWarnings}
                      schedules={assetSchedules}
                      selectedAssets={selectedAssets}
                      sportsCampaignIds={sportsCampaignIds}
                      strategyLabels={strategyLabels}
                      onScheduleChange={handleAssetScheduleChange}
                    />
                )}
                {!(isLoading || (isFetching && viableAssets.length === 0)) &&
                    viableAssets.length !== 0 &&
                  approvalStatus !== ASSET_STATUSES.PENDING && (
                    <AssetTable
                      assets={viableAssets}
                      campaigns={campaignsDataForDisplay}
                      handleAssetSelect={handleAssetSelect}
                      isReleaseDatesFetching={isAssetListTitlesFetching}
                      releaseDatesByGti={releaseDatesByGti}
                      selectedAssets={selectedAssets}
                      setAssetDetail={setAssetDetail}
                      setAssetDetailView={setAssetDetailView}
                      setSelectedAssets={setSelectedAssets}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      strategyLabels={strategyLabels}
                      onSortChange={handleSortChange}
                    />
                )}
              </Box>
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1, backgroundColor: '#FFFFFF', borderTop: '1px solid #D1D5DB', padding: '8px 16px' }}>
                <TablePagination
                  currentPage={pagination[approvalStatus].page}
                  itemsPerPage={pagination[approvalStatus].pageSize}
                  itemsPerPageOptions={itemsPerPageOptions}
                  pageCount={pagination[approvalStatus].pageCount}
                  onItemsPerPageChange={(pageSize: number) => {
                    setPagination((prev) => ({
                      ...prev,
                      [approvalStatus]: {
                        ...prev[approvalStatus],
                        page: 1,
                        pageSize,
                      },
                    }));
                  }}
                  onPageChange={(newPage: number) => {
                    setPagination((prev) => ({
                      ...prev,
                      [approvalStatus]: {
                        ...prev[approvalStatus],
                        page: newPage,
                      },
                    }));
                  }}
                />
                </div>
              </>
                )}
      </Column>
      <BulkScheduleOverrideModal
          groups={bulkScheduleGroups}
          open={bulkScheduleModalOpen}
          onApply={handleBulkScheduleApply}
          onClose={() => { setBulkScheduleModalOpen(false); }}
        />
        {currentAction && (
          <AssetApprovalModal
            action={currentAction}
            assetBatches={chunk(selectedAssets, ASSET_UPDATE_BATCH_SIZE)}
            campaigns={campaignsDataForDisplay}
            isLoading={isUpdating}
            open={assetApprovalModal}
            rejectedAssetsOnProcess={chunk(
              rejectedAssets,
              ASSET_UPDATE_BATCH_SIZE,
            )}
            scheduleWarnings={scheduleWarnings}
            strategyLabels={strategyLabels}
            onCancel={() => {
              setAssetApprovalModal(false);
              setCurrentAction(undefined);
            }}
            onConfirm={() => {
              return handleStatusUpdateV2(currentAction);
            }}
          />
        )}
        {currentAction && assetDetail && (
          <AssetDetailApprovalModal
            action={currentAction}
            asset={assetDetail}
            isLoading={isUpdating}
            open={assetDetailApprovalModal}
            strategyLabels={strategyLabels}
            onCancel={() => {
              setAssetDetailApprovalModal(false);
              setCurrentAction(undefined);
            }}
            onConfirm={() => {
              return handleSingleStatusUpdate(currentAction, assetDetail);
            }}
          />
        )}
      </div>
  );
};
